type PromiseWithStatusValue<Result, Variables extends unknown[]> =
	| {
			status: "initial";
	  }
	| { status: "loading"; promise: Promise<Result>; variables: Variables }
	| { status: "success"; result: Result }
	| { status: "error"; error: unknown };

export interface PromiseWithStatus<Result, Variables extends unknown[]> {
	call(...variables: Variables): Promise<Result>;
	value: PromiseWithStatusValue<Result, Variables>;
}

export function createPromiseWithStatus<Result, Variables extends unknown[]>(
	callback: (...variables: Variables) => Promise<Result>,
	onSuccess?: (result: Result) => void,
	onError?: (error: unknown) => void,
): PromiseWithStatus<Result, Variables> {
	let value = $state<PromiseWithStatusValue<Result, Variables>>({ status: "initial" });

	return {
		async call(...variables: Variables): Promise<Result> {
			const promise = callback(...variables);
			value = { status: "loading", promise, variables };
			return promise
				.then((result) => {
					if (value.status === "loading" && promise === value.promise) {
						value = { status: "success", result };
						onSuccess?.(result);
					}
					return result;
				})
				.catch((error: unknown) => {
					if (value.status === "loading" && promise === value.promise) {
						value = { status: "error", error };
						onError?.(error);
					}
					throw error;
				});
		},
		get value(): PromiseWithStatusValue<Result, Variables> {
			return value;
		},
	};
}
